import {
    Button,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Icon,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from "@elit-hair/elit-ui";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronRight, TbDotsVertical, TbPencil, TbPin, TbPlus, TbTrash } from "react-icons/tb";

import { CustomLinkDialog, type CustomLinkFormData } from "./custom-link-dialog";
import { CustomLinkIcon } from "./custom-link-icon";
import { type CustomLink, useCustomLinks } from "./use-custom-links";

function CustomLinkActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
    const { t } = useTranslation();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover/link:opacity-100 data-[state=open]:opacity-100"
                    aria-label={t("custom_link_actions")}
                >
                    <Icon icon={TbDotsVertical} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-dialog!">
                <DropdownMenuItem onClick={onEdit}>
                    <Icon icon={TbPencil} className="mr-2" />
                    {t("edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                    <Icon icon={TbTrash} className="mr-2" />
                    {t("delete")}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function PinnedUrlRow({
    url,
    label,
    iconName,
    actions,
    onOpen,
}: {
    url: string;
    label: string;
    iconName: string | null;
    actions?: ReactNode;
    onOpen?: () => void;
}) {
    return (
        <div className="group/link flex w-full min-w-0 items-center gap-1">
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onOpen}
                className="flex min-w-0 flex-1 items-center gap-2"
            >
                <CustomLinkIcon url={url} iconName={iconName} />
                <span className="truncate">{label}</span>
            </a>
            {actions}
        </div>
    );
}

export function CustomLinksPanel() {
    const { t } = useTranslation();
    const { state, isMobile } = useSidebar();
    const collapsed = state === "collapsed" && !isMobile;

    const { links, addLink, updateLink, removeLink } = useCustomLinks();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLink, setEditingLink] = useState<CustomLink | null>(null);
    const [popoverOpen, setPopoverOpen] = useState(false);

    const handleAddClick = useCallback(() => {
        setEditingLink(null);
        setPopoverOpen(false);
        setDialogOpen(true);
    }, []);

    const handleEditClick = useCallback((link: CustomLink) => {
        setEditingLink(link);
        setPopoverOpen(false);
        setDialogOpen(true);
    }, []);

    const handleDialogSave = useCallback(
        (data: CustomLinkFormData) => {
            if (editingLink) {
                updateLink(editingLink.id, data);
            } else {
                addLink(data);
            }
            setDialogOpen(false);
            setEditingLink(null);
        },
        [editingLink, addLink, updateLink]
    );

    const urlRows = links.map((link) => {
        const row = (
            <PinnedUrlRow
                url={link.url}
                label={link.label}
                iconName={link.iconName}
                onOpen={collapsed ? () => setPopoverOpen(false) : undefined}
                actions={
                    <CustomLinkActions onEdit={() => handleEditClick(link)} onDelete={() => removeLink(link.id)} />
                }
            />
        );

        if (collapsed) {
            return (
                <div key={link.id} className="rounded-md px-2 py-1.5 hover:bg-sidebar-accent">
                    {row}
                </div>
            );
        }

        return (
            <SidebarMenuSubItem key={link.id}>
                <SidebarMenuSubButton asChild>{row}</SidebarMenuSubButton>
            </SidebarMenuSubItem>
        );
    });

    const addButton = collapsed ? (
        <button
            type="button"
            onClick={handleAddClick}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-sidebar-accent"
        >
            <Icon icon={TbPlus} className="shrink-0" />
            <span>{t("custom_link_add")}</span>
        </button>
    ) : (
        <SidebarMenuSubItem>
            <SidebarMenuSubButton onClick={handleAddClick}>
                <Icon icon={TbPlus} className="shrink-0" />
                <span className="text-muted-foreground">{t("custom_link_add")}</span>
            </SidebarMenuSubButton>
        </SidebarMenuSubItem>
    );

    const dialog = (
        <CustomLinkDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            editingLink={editingLink}
            onSave={handleDialogSave}
        />
    );

    if (collapsed) {
        return (
            <>
                <SidebarMenu>
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <SidebarMenuItem>
                            <PopoverTrigger asChild>
                                <SidebarMenuButton tooltip={t("pinned_links")}>
                                    <Icon icon={TbPin} size={20} />
                                    <span>{t("pinned_links")}</span>
                                </SidebarMenuButton>
                            </PopoverTrigger>
                            <PopoverContent side="right" align="start" sideOffset={8} className="w-56 p-1">
                                <div className="flex flex-col gap-0.5">
                                    {urlRows}
                                    {addButton}
                                </div>
                            </PopoverContent>
                        </SidebarMenuItem>
                    </Popover>
                </SidebarMenu>
                {dialog}
            </>
        );
    }

    return (
        <>
            <SidebarMenu>
                <Collapsible asChild className="group/collapsible">
                    <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                            <SidebarMenuButton>
                                <Icon icon={TbPin} size={20} />
                                <span>{t("pinned_links")}</span>
                                <Icon
                                    icon={TbChevronRight}
                                    className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                                />
                            </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <SidebarMenuSub>
                                {urlRows}
                                {addButton}
                            </SidebarMenuSub>
                        </CollapsibleContent>
                    </SidebarMenuItem>
                </Collapsible>
            </SidebarMenu>
            {dialog}
        </>
    );
}
